import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { IoSearchOutline, IoFilterOutline, IoAddOutline, IoTimeOutline, IoPlayOutline, IoCheckmarkCircleOutline, IoInformationCircleOutline } from "react-icons/io5";

import DebounceInput from "../../../../../components/DebounceInput";
import api from "../../../../../services/api";
import MultiSelect from "../../../../../components/MultiSelect";
import { MultiAction } from "./MultiAction";

const TopMenu = ({ filters, setFilters, project, epics, selectedEpics, setSelectedEpic, setIsEpicEditModalOpen, fetchEpics, setSelectedEpics }) => {
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const handleMultipleDelete = async () => {
    try {
      if (!window.confirm("Are you sure you want to delete selected epics?")) return;

      for (let i = 0; i < selectedEpics.length; i++) {
        const { ok } = await api.remove(`/epic/${selectedEpics[i]._id}`);
        if (!ok) {
          toast.error(`Failed to delete epic ${selectedEpics[i]._id}`);
          continue;
        }
      }

      toast.success("Epics deleted successfully");
      fetchEpics();
      setSelectedEpics([]);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete epics");
    }
  };

  console.log(filters);

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-3 flex items-center gap-3 border-b border-gray-100">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <IoSearchOutline className="h-4 w-4 text-gray-400" />
          </div>
          <DebounceInput
            debounce={300}
            className="block w-full pl-10 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Search epics..."
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
          />
        </div>

        <div className="flex items-center gap-2">
          <div className="gap-2 w-44">
            <SelectBudgets value={filters.budget_id} onChange={(e) => setFilters((f) => ({ ...f, budget_id: e }))} project={project} />
          </div>
          <div className="gap-2 w-44">
            <SelectModule value={filters.modules} onChange={(modules) => setFilters((f) => ({ ...f, modules }))} project={project} />
          </div>
          <div className="gap-2 w-44">
            <MultiSelect
              id="select-status"
              options={["TODO", "IN_PROGRESS", "DONE", "NEED_INFO"].map((status) => ({
                value: status,
                label: status,
                icon: getStatusIcon(status),
              }))}
              values={filters.statuses?.map((status) => ({ value: status, label: status }))}
              onSelectedChange={(e) => setFilters((f) => ({ ...f, statuses: e.map((e) => e.value) }))}
              placeholder="Status"
              buttonClassName="px-3 py-1.5"
            />
          </div>

          <button
            onClick={() => setShowMoreFilters(!showMoreFilters)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md transition-colors
              ${showMoreFilters ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}>
            <IoFilterOutline className="h-4 w-4" />
            More Filters
          </button>

          <div className="flex items-center gap-2 pl-2 border-l border-gray-200 ">
            <button
              onClick={async () => {
                const { ok, data } = await api.post("/epic", { project_id: project._id });
                if (!ok) return toast.error("Failed to create epic");
                toast.success("Epic created successfully");
                fetchEpics();
                setSelectedEpic(data);
                setIsEpicEditModalOpen(true);
              }}
              className="inline-flex w-full items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
              <IoAddOutline className="h-4 w-4" />
              New Epic
            </button>
            <MultiAction selectedEpics={selectedEpics} epics={epics} project={project} handleMultipleDelete={handleMultipleDelete} />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showMoreFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-gray-100">
            <div className="p-3 grid grid-cols-4 gap-3">
              <MultiSelect
                id="select-resource-profile"
                options={["DEVELOPER", "EXPERIMENTED DEVELOPER", "CTO", "PRODUCT", "IA ENGINEER", "DevOps"].map((r) => ({
                  value: r,
                  label: r,
                }))}
                values={filters.resource_profile?.map((r) => ({ value: r, label: r }))}
                onSelectedChange={(e) => setFilters((f) => ({ ...f, resource_profile: e.map((e) => e.value) }))}
                placeholder="Resource Profile"
                className="w-full"
                buttonClassName="px-3 py-1.5"
              />
              <MultiSelect
                id="select-priority"
                options={["NOW", "NEXT", "LATER"].map((p) => ({
                  value: p,
                  label: p,
                  className: getPriorityClass(p), // Helper function for priority-specific styling
                }))}
                values={filters.priorities?.map((p) => ({ value: p, label: p }))}
                onSelectedChange={(e) => setFilters((f) => ({ ...f, priorities: e.map((e) => e.value) }))}
                placeholder="Priority"
                className="w-full"
                buttonClassName="px-3 py-1.5"
              />
              <SelectAssigned value={filters.assigned} onChange={(e) => setFilters((f) => ({ ...f, assigned: e }))} project={project} className="w-full" />
              <select
                value={filters.is_sent_to_backlog || ""}
                onChange={(e) => setFilters((f) => ({ ...f, is_sent_to_backlog: e.target.value }))}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                <option value="">All Backlog Status</option>
                <option value="yes">In Backlog</option>
                <option value="no">Not in Backlog</option>
              </select>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const getStatusIcon = (status) => {
  switch (status) {
    case "TODO":
      return <IoTimeOutline className="h-4 w-4" />;
    case "IN_PROGRESS":
      return <IoPlayOutline className="h-4 w-4" />;
    case "DONE":
      return <IoCheckmarkCircleOutline className="h-4 w-4" />;
    case "NEED_INFO":
      return <IoInformationCircleOutline className="h-4 w-4" />;
    default:
      return null;
  }
};

const getPriorityClass = (priority) => {
  switch (priority) {
    case "NOW":
      return "bg-blue-50 text-blue-700";
    case "NEXT":
      return "bg-yellow-50 text-yellow-700";
    case "LATER":
      return "bg-gray-50 text-gray-700";
    default:
      return "";
  }
};

const SelectAssigned = ({ value, onChange, project }) => {
  const [options, setOptions] = useState([]);
  const [lastFetch, setLastFetch] = useState(0);

  async function fetchAssigned() {
    const { data } = await api.post("/epic/aggregate", { project_id: project._id, key: "assigned" });
    const arr = data.filter((e) => e._id !== null).map((e) => ({ label: e._id, value: e._id, count: e.count }));
    setOptions(arr);
    setLastFetch(Date.now());
  }

  useEffect(() => {
    fetchAssigned();
  }, [project._id]);

  useEffect(() => {
    function handleFocus() {
      if (Date.now() - lastFetch > 5000) {
        fetchAssigned();
      }
    }
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [lastFetch]);

  return (
    <MultiSelect
      id="select-assigned"
      options={options}
      value={value ? [{ value, label: value }] : []}
      onSelectedChange={(e) => onChange(e[0]?.value || null)}
      placeholder="Assigned"
      buttonClassName="px-3 py-1.5"
    />
  );
};

const SelectModule = ({ value, onChange, project }) => {
  const [options, setOptions] = useState([]);
  const [lastFetch, setLastFetch] = useState(0);

  async function fetchModules() {
    const { data } = await api.post("/epic/aggregate", { project_id: project._id, key: "module" });
    const arr = data.filter((e) => e._id !== null).map((e) => ({ label: e._id, value: e._id, count: e.count }));
    setOptions(arr);
    setLastFetch(Date.now());
  }

  useEffect(() => {
    fetchModules();
  }, [project._id]);

  useEffect(() => {
    function handleFocus() {
      if (Date.now() - lastFetch > 5000) {
        fetchModules();
      }
    }
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [lastFetch]);

  // Convert single value or array to proper format for MultiSelect
  const selectedValues = Array.isArray(value) ? value.map((v) => ({ value: v, label: v })) : value ? [{ value, label: value }] : [];

  return (
    <MultiSelect
      id="select-module"
      options={options}
      values={selectedValues}
      onSelectedChange={(selected) => onChange(selected.map((s) => s.value))}
      placeholder="Module"
      buttonClassName="px-3 py-1.5"
    />
  );
};

const SelectBudgets = ({ value, onChange, project }) => {
  const [options, setOptions] = useState([]);

  async function fetchBudgets() {
    const { data } = await api.post("/budget/search", { projectId: project._id, status: "active" });
    setOptions(data);
  }

  useEffect(() => {
    fetchBudgets();
  }, [project._id]);

  return (
    <select
      className={`w-full px-3 py-1.5 justify-between items-center gap-4 border border-gray-200 text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
      value={value}
      onChange={(e) => onChange(e.target.value)}>
      <option value="">All budgets</option>
      {options.map((option) => (
        <option key={option._id} value={option._id}>
          {option.name}
        </option>
      ))}
    </select>
  );
};

export default TopMenu;
